import React from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { VerticalCostRevenueBar, MarginBar, HorizontalRevenueBar } from "@/components/Charts";
import { fmtCurrency, fmtNumber, fmtPct, fmtDecimal } from "@/lib/format";
import { SummaryBanner, SectionBox, EmptyBlock } from "@/components/DashboardBits";

export default function CostResourcePressure({ analysis }) {
  const econ = analysis.department_economics || [];
  const procs = analysis.procedure_economics || [];
  const procurement = analysis.procurement_breakdown || [];
  const hasCost = analysis.has_cost;

  const worstMargin = [...econ].sort(
    (a, b) => (a.margin_pct ?? 999) - (b.margin_pct ?? 999)
  )[0];
  const summary = worstMargin && worstMargin.margin_pct !== undefined
    ? `${worstMargin.name} shows the tightest margin at ${fmtPct(worstMargin.margin_pct)}. Procurement spend totals ${fmtCurrency(
        procurement.reduce((s, p) => s + (p.spend || 0), 0)
      )} across ${procurement.length} categor${procurement.length === 1 ? "y" : "ies"}.`
    : "Operational cost pressure across OT minutes, staffing, procurement and cost-to-serve.";

  const otByProc = [...procs]
    .filter((p) => p.avg_ot !== undefined && p.avg_ot !== null)
    .sort((a, b) => (b.avg_ot || 0) - (a.avg_ot || 0))
    .slice(0, 10)
    .map((p) => ({ name: p.name, ot: p.avg_ot, revenue: p.avg_revenue }));

  const staffByDept = econ
    .filter((d) => d.staff_hours !== undefined && d.staff_hours !== null)
    .map((d) => ({ name: d.name, hours: d.staff_hours, revenue: d.revenue }));

  return (
    <div className="space-y-8">
      <SummaryBanner title="Where Money Is Lost" text={summary} testId="cost-summary-banner" />

      {hasCost && econ.some((d) => d.total_cost !== undefined) && (
        <SectionBox title="Cost vs Revenue by Department" eyebrow="/ Head to head" testId="cost-vs-rev">
          <VerticalCostRevenueBar
            data={econ.slice(0, 10).map((d) => ({
              name: d.name,
              revenue: d.revenue,
              cost: d.total_cost,
            }))}
          />
        </SectionBox>
      )}

      {hasCost && econ.some((d) => d.margin !== undefined) && (
        <SectionBox title="Margin Pressure Zones" eyebrow="/ Profit / loss" testId="margin-zones">
          <MarginBar data={econ.slice(0, 12).map((d) => ({ name: d.name, margin: d.margin }))} />
        </SectionBox>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {otByProc.length ? (
          <SectionBox title="OT Minutes by Procedure" eyebrow="/ Scarcest resource" testId="ot-by-proc">
            <HorizontalRevenueBar data={otByProc} dataKey="ot" color="#DC2626" />
            <div className="text-xs text-gray-500 mt-3 font-mono-metric">
              Average OT time per case — high time with low revenue signals scheduling opportunity.
            </div>
          </SectionBox>
        ) : null}

        {staffByDept.length ? (
          <SectionBox title="Staff Hours by Department" eyebrow="/ Operating cost driver" testId="staff-by-dept">
            <HorizontalRevenueBar
              data={staffByDept.slice(0, 10).map((d) => ({ name: d.name, hours: d.hours }))}
              dataKey="hours"
              color="#6366F1"
            />
          </SectionBox>
        ) : null}
      </div>

      {procurement.length ? (
        <SectionBox
          title="Procurement Spend by Category"
          eyebrow="/ Supply chain"
          description="Categories sorted by spend. Implants and pharmacy typically dominate; consolidation compounds savings."
          testId="procurement-section"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {procurement.map((p, i) => (
              <div
                key={p.name}
                className="border border-gray-200 p-5"
                data-testid={`procurement-card-${i}`}
              >
                <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#5E3BFF] mb-3">
                  / {p.name}
                </div>
                <div className="font-display text-2xl font-black tracking-tighter text-gray-950">
                  {fmtCurrency(p.spend)}
                </div>
                <div className="mt-3 pt-3 border-t border-gray-100 text-xs space-y-1.5">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Volume</span>
                    <span className="font-mono-metric">{fmtNumber(p.volume)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Avg / case</span>
                    <span className="font-mono-metric">{fmtCurrency(p.avg_spend)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </SectionBox>
      ) : null}

      <SectionBox
        title="Cost-to-Serve by Department"
        eyebrow="/ Full P&L view"
        testId="cost-to-serve-table"
      >
        {econ.length ? (
          <Table>
            <TableHeader>
              <TableRow className="border-b border-gray-200">
                <TableHead className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-600">
                  Department
                </TableHead>
                <TableHead className="text-right text-[10px] uppercase tracking-[0.2em] font-bold text-gray-600">
                  Revenue
                </TableHead>
                <TableHead className="text-right text-[10px] uppercase tracking-[0.2em] font-bold text-gray-600">
                  Total Cost
                </TableHead>
                <TableHead className="text-right text-[10px] uppercase tracking-[0.2em] font-bold text-gray-600">
                  Margin
                </TableHead>
                <TableHead className="text-right text-[10px] uppercase tracking-[0.2em] font-bold text-gray-600">
                  Margin %
                </TableHead>
                {econ[0]?.avg_los !== undefined && (
                  <TableHead className="text-right text-[10px] uppercase tracking-[0.2em] font-bold text-gray-600">
                    Avg LOS
                  </TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {econ.slice(0, 15).map((d) => (
                <TableRow key={d.name} className="border-b border-gray-100">
                  <TableCell className="font-medium">{d.name}</TableCell>
                  <TableCell className="text-right font-mono-metric">{fmtCurrency(d.revenue)}</TableCell>
                  <TableCell className="text-right font-mono-metric">
                    {d.total_cost !== undefined ? fmtCurrency(d.total_cost) : "—"}
                  </TableCell>
                  <TableCell
                    className={`text-right font-mono-metric font-semibold ${
                      d.margin !== undefined && d.margin < 0 ? "text-[#DC2626]" : "text-green-700"
                    }`}
                  >
                    {d.margin !== undefined ? fmtCurrency(d.margin) : "—"}
                  </TableCell>
                  <TableCell
                    className={`text-right font-mono-metric ${
                      d.margin_pct !== undefined && d.margin_pct < 10 ? "text-[#DC2626]" : "text-gray-700"
                    }`}
                  >
                    {d.margin_pct !== undefined ? fmtPct(d.margin_pct) : "—"}
                  </TableCell>
                  {d.avg_los !== undefined && (
                    <TableCell className="text-right font-mono-metric">
                      {fmtDecimal(d.avg_los)}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <EmptyBlock text="Department economics unavailable." />
        )}
      </SectionBox>
    </div>
  );
}
